import React, {useContext, useEffect, useRef, useState} from 'react'
import moment from 'moment'
import {KTSVG} from '../../../_metronic/helpers'
import ToolbarCustom from '../../../_metronic/layout/components/toolbar/toolbars/ToolbarCustom'

import { CustomersContext, ProductsContext } from '../../context'
import PurchaseDetailModal from './PurchaseDetailModal'
import { GridActionsCellItem } from '@mui/x-data-grid'
import DataGridComponent from '../../components/DataGrid'
import { buildPurchasesExport, paymentMethodLabels } from '../../utils/purchases'
import { EXPORT_DATE_FORMAT_SHORT, itemWithId } from '../../utils'
import UserSegmentEditor from '../../components/UserSegmentEditor'

const seasonOptions = [
  '2022/2023',
  '2023/2024',
  '2024/2025',
  '2025/2026',
  '2026/2027',
]

const getSeasonRange = (season) => {
  if(!season) {
    return { rangeStart: null, rangeEnd: null }
  }
  const [startYear, endYear] = season.split('/').map(Number)
  const rangeStart = moment(`${startYear}-07-01`).startOf('day').valueOf()
  const rangeEnd = moment(`${endYear}-06-30`).endOf('day').valueOf()
  return { rangeStart, rangeEnd }
}

const getDefaultSeason = () => {
  const now = moment()
  const year = now.year()
  const isAfterJune = now.month() >= 6
  const startYear = isAfterJune ? year : year - 1
  const endYear = isAfterJune ? year + 1 : year
  const currentSeason = `${startYear}/${endYear}`
  if(seasonOptions.includes(currentSeason)) {
    return currentSeason
  }
  return seasonOptions[seasonOptions.length - 1]
}

const PurchasesPage = () => {
  
  const {
    customers,
    customersLoaded,
    fetchCustomers,
    purchases,
    purchasesLoaded,
    purchasesLoading,
    fetchPurchases,
    paymentRequests,
    paymentRequestsLoaded,
    fetchPaymentRequests,
    partners,
    partnersLoaded,
    fetchPartners,
  } = useContext(CustomersContext)

  const {
    resorts,
    resortsLoaded,
    fetchResorts,
    domains,
    domainsLoaded,
    fetchDomains,
    durations,
    durationsLoaded,
    fetchDurations,
    genericProducts,
    genericProductsLoaded,
    specificProducts,
    specificProductsLoaded,
    localProducts,
    localProductsLoaded,
    legacyProducts,
    legacyProductsLoaded,
    fetchProducts,
    resellers,
    resellersLoaded,
    fetchResellers,
    regions,
    regionsLoaded,
    fetchRegions,
  } = useContext(ProductsContext)

  const [currentPurchase, setCurrentPurchase] = useState(null)
  const [filteredPurchases, setFilteredPurchases] = useState([])
  const [validPurchases, setValidPurchases] = useState([])
  const [showExportModal, setShowExportModal] = useState(false)
  // const [loadingExport, setLoadingExport] = useState(false)
  const [exportRangeStart, setExportRangeStart] = useState(null)
  const [exportRangeEnd, setExportRangeEnd] = useState(null)
  const [search, setSearch] = useState('')
  const [selectedSeason, setSelectedSeason] = useState(getDefaultSeason)
  const mostRecentSeason = getDefaultSeason()

  // summary of each prop based on existing purchases - to be used in export
  const [purchaseSummaryGenericProducts, setPurchaseSummaryGenericProducts] = useState([])
  const [purchaseSummarySpecificProducts, setPurchaseSummarySpecificProducts] = useState([])
  const [purchaseSummaryResorts, setPurchaseSummaryResorts] = useState([])
  const [purchaseSummaryDomains, setPurchaseSummaryDomains] = useState([])
  const [purchaseSummaryDurations, setPurchaseSummaryDurations] = useState([])
  const [purchaseSummaryLanguages, setPurchaseSummaryLanguages] = useState([])
  const [purchaseSummaryCountries, setPurchaseSummaryCountries] = useState([])
  const [purchaseSummaryPaymentMethods, setPurchaseSummaryPaymentMethods] = useState([])
  const [purchaseSummaryResellers, setPurchaseSummaryResellers] = useState([])
  const [purchaseSummaryRegions, setPurchaseSummaryRegions] = useState([])

  const [exportUserSegment, setExportUserSegment] = useState({
    product: null,
    numberOfBeneficiaries: {
      relation: 'moreThan',
      value: 0,
    },
    durationType: null, // any, days or season
    duration: {
      relation: 'moreThan',
      value: 0,
    },
    language: null,
    country: null,
    resort: null,
    resorts: [],

    paymentMethod: null,
    memberOfOrionTnNetwork: null,
    orionTnOfficialDistributor: null,
    orionTnOfficialLeadGenerator: null,
    apiPurchase: null,
    apiPartner: null,
  })

  useEffect(() => {
    if(
      !purchasesLoaded ||
      !paymentRequestsLoaded ||
      !resortsLoaded ||
      !durationsLoaded ||
      !domainsLoaded ||
      !genericProductsLoaded ||
      !specificProductsLoaded ||
      !resellersLoaded ||
      !regionsLoaded
    ) {
      return
    }
    const genericProductsSet = new Set()
    const specificProductsSet = new Set()
    const resortsSet = new Set()
    const domainsSet = new Set()
    const durationsSet = new Set()
    const languagesSet = new Set()
    const countriesSet = new Set()
    const paymentMethodsSet = new Set()
    const resellersSet = new Set()
    const regionsSet = new Set()
    for(let purchase of purchases) {
      const paymentRequest = itemWithId(paymentRequests, purchase.id)
      const { product_generic, product_specific, resort, domain, duration, language } = purchase
      const country = paymentRequest?.card?.country
      const paymentMethod = paymentRequest?.payment_method
      const resortObject = itemWithId(resorts, resort)
      const region = itemWithId(regions, resortObject?.region)
      const reseller = resellers.find(reseller => reseller.resorts?.includes(resort))
      if(product_generic) {
        genericProductsSet.add(product_generic)
      }
      if(product_specific) {
        specificProductsSet.add(product_specific)
      }
      if(resort) {
        resortsSet.add(resort)
      }
      if(domain) {
        domainsSet.add(domain)
      }
      if(duration) {
        durationsSet.add(duration)
      }
      if(language) {
        languagesSet.add(language)
      }
      if(country) {
        countriesSet.add(country)
      }
      if(paymentMethod) {
        paymentMethodsSet.add(paymentMethod)
      }
      if(region) {
        regionsSet.add(region.id)
      }
      if(reseller) {
        resellersSet.add(reseller.id)
      }
    }
    // set state for export
    setPurchaseSummaryGenericProducts(Array.from(genericProductsSet).map(id => itemWithId(genericProducts, id)).filter(p => p)) // filter out null values
    setPurchaseSummarySpecificProducts(Array.from(specificProductsSet).map(id => itemWithId(specificProducts, id)).filter(p => p)) // filter out null values
    setPurchaseSummaryResorts(Array.from(resortsSet).map(id => itemWithId(resorts, id)).filter(r => r)) // filter out null values
    setPurchaseSummaryDomains(Array.from(domainsSet).map(id => itemWithId(domains, id)).filter(d => d)) // filter out null values
    setPurchaseSummaryDurations(Array.from(durationsSet).map(id => itemWithId(durations, id)).filter(d => d)) // filter out null values
    setPurchaseSummaryLanguages(Array.from(languagesSet))
    setPurchaseSummaryCountries(Array.from(countriesSet))
    setPurchaseSummaryPaymentMethods(Array.from(paymentMethodsSet).map(pm => pm)) // filter out null values
    setPurchaseSummaryResellers(Array.from(resellersSet).map(id => itemWithId(resellers, id)).filter(r => r)) // filter out null values
    setPurchaseSummaryRegions(Array.from(regionsSet).map(id => itemWithId(regions, id)).filter(r => r)) // filter out null values
  }, [
    resorts,
    resortsLoaded,
    durations,
    durationsLoaded,
    domains,
    domainsLoaded,
    genericProducts,
    genericProductsLoaded,
    specificProducts,
    specificProductsLoaded,
    purchases,
    purchasesLoaded,
    paymentRequests,
    paymentRequestsLoaded,
    resellers,
    resellersLoaded,
    regions,
    regionsLoaded,
  ])

  useEffect(() => {
    if(!customersLoaded) {
      fetchCustomers()
    }
  }, [customersLoaded, fetchCustomers])

  useEffect(() => {
    const { rangeStart, rangeEnd } = getSeasonRange(selectedSeason)
    fetchPurchases({
      rangeStart,
      rangeEnd,
      seasonKey: selectedSeason,
      isMostRecent: selectedSeason === mostRecentSeason
    })
  }, [selectedSeason, mostRecentSeason])

  useEffect(() => {
    if(!paymentRequestsLoaded) {
      fetchPaymentRequests()
    }
  }, [paymentRequestsLoaded])

  useEffect(() => {
    if(!partnersLoaded) {
      fetchPartners()
    }
  }, [partnersLoaded, fetchPartners])

  useEffect(() => {
    if(!resortsLoaded) {
      fetchResorts()
    }
  }, [resortsLoaded, fetchResorts])

  useEffect(() => {
    if(!domainsLoaded) {
      fetchDomains()
    }
  }, [domainsLoaded, fetchDomains])

  useEffect(() => {
    if(!durationsLoaded) {
      fetchDurations()
    }
  }, [durationsLoaded, fetchDurations])

  useEffect(() => {
    if(!genericProductsLoaded) {
      fetchProducts('generic')
    }
  }, [genericProductsLoaded, fetchProducts])

  useEffect(() => {
    if(!specificProductsLoaded) {
      fetchProducts('specific')
    }    
  }, [specificProductsLoaded, fetchProducts])

  useEffect(() => {
    if(!localProductsLoaded) {
      fetchProducts('local')
    }    
  }, [localProductsLoaded, fetchProducts])

  useEffect(() => {
    if(!legacyProductsLoaded) {
      fetchProducts('legacy')
    }    
  }, [legacyProductsLoaded, fetchProducts])

  useEffect(() => {
    if(!resellersLoaded) {
      fetchResellers()
    }
  }, [resellersLoaded, fetchResellers])

  useEffect(() => {
    if(!regionsLoaded) {
      fetchRegions()
    }
  }, [regionsLoaded, fetchRegions])

  useEffect(() => {
    // update current purchase if data changes
    if(currentPurchase?.id) {
      const purchase = purchases.find(p => p.id === currentPurchase.id)
      setCurrentPurchase(purchase)
    }
  }, [purchases])

  const onViewClick = (purchase) => {
    setCurrentPurchase(purchase)
  }

  const convertToTableItem = (purchase) => {
    const paymentRequest = itemWithId(paymentRequests, purchase.id)
    const apiPartner = itemWithId(partners, purchase.api_partner)
    const invoiceRequired = !purchase.api_purchase || purchase.hosted_payment
    const documentsValid = Boolean(purchase.contract_url) && (!invoiceRequired || Boolean(purchase.invoice_url))
    return {
      ...purchase,
      customer_name: `${purchase.buyer.firstname} ${purchase.buyer.lastname}`,
      customer_email: purchase.buyer.email,
      product_label: purchase.product_name,
      metadata_activity: typeof purchase.purchase_metadata?.activity === 'string' ? purchase.purchase_metadata.activity : '',
      metadata_region: typeof purchase.purchase_metadata?.region === 'string' ? purchase.purchase_metadata.region : '',
      paid_at_label: purchase.paid_at ? moment(purchase.paid_at).format('DD/MM/YYYY HH:mm') : purchase.meta?.updated ? moment(purchase.meta.updated).format('DD/MM/YYYY HH:mm') : 'N/A',
      documents_valid: documentsValid,
      resort_label: itemWithId(resorts, purchase.resort)?.name?.fr || 'N/A',
      domain_label: itemWithId(domains, purchase.domain)?.title?.fr || 'N/A',
      duration_label: itemWithId(durations, purchase.duration)?.title?.fr || 'N/A',
      language_label: purchase.language || 'N/A',
      country_label: paymentRequest?.card?.country || 'N/A',
      payment_method_label: paymentRequest?.payment_method ? paymentMethodLabels[paymentRequest.payment_method] || paymentRequest.payment_method : 'N/A',
      api_partner_label: apiPartner?.name?.fr || apiPartner?.name || apiPartner?.company_name || apiPartner?.title || 'N/A',
    }
  }

  useEffect(() => {
    let fp = [...validPurchases]

    if(search) {
      const searchTokens = search.split(' ').filter(t => t.length > 0)
      fp = fp.filter(purchase => {
        const fields = [purchase.buyer.firstname, purchase.buyer.lastname, purchase.buyer.email, purchase.id]
        // check if all tokens are found in at least one field
        for(let token of searchTokens) {
          if(!fields.find(f => f.toLowerCase().includes(token.toLowerCase()))) {
            return false
          }
        }
        return true
      })
    }

    fp.sort((a, b) => {
      const aDate = a.paid_at || a.meta?.updated || a.meta?.created || 0
      const bDate = b.paid_at || b.meta?.updated || b.meta?.created || 0
      if(aDate < bDate) return 1
      if(aDate > bDate) return -1
      return 0
    })
    setFilteredPurchases(fp)
  }, [customers, validPurchases, search])

  useEffect(() => {

    let fp = []
    for(let p of purchases) {
      if(p.status === 'paid' || p.status === 'refunded') {
        fp.push(p)
      }
    }
    setValidPurchases(fp)
  }, [purchases])

  const isExportReady = !(!paymentRequestsLoaded || !legacyProductsLoaded || !localProductsLoaded || !specificProductsLoaded || !genericProductsLoaded || !resortsLoaded || !domainsLoaded || !durationsLoaded || !resellersLoaded || !regionsLoaded || !partnersLoaded || !purchasesLoaded)

  const promptExport = async () => {
    
    if(!isExportReady) {
      alert('Wait for data to be loaded. Please try again in a moment. Still loading: ' + [
        !paymentRequestsLoaded && 'payment requests',
        !legacyProductsLoaded && 'legacy products',
        !localProductsLoaded && 'local products',
        !specificProductsLoaded && 'specific products',
        !genericProductsLoaded && 'generic products',
        !resortsLoaded && 'resorts',
        !domainsLoaded && 'domains',
        !durationsLoaded && 'durations',
        !resellersLoaded && 'resellers',
        !regionsLoaded && 'regions',
        !partnersLoaded && 'partners',
        !purchasesLoaded && 'purchases',
      ].filter(s => s).join(', '))
      return
    }

    setShowExportModal(true)
    // setLoadingExport(true)
    // if(!paymentRequestsLoaded) {
    //   await fetchPaymentRequestsWithStateCheck()
    // }
    // setLoadingExport(false)    
  }

  // const fetchPaymentRequestsWithStateCheck = () => {
  //   return new Promise((resolve) => {
  //     // Check if payment requests are already loaded
  //     if (paymentRequestsLoadedRef.current) {
  //       resolve()
  //     } else {
  //       // If not loaded, fetch them
  //       fetchPaymentRequests()

  //       // and wait for the state to update
  //       const interval = setInterval(() => {
  //         if (paymentRequestsLoadedRef.current) {
  //           clearInterval(interval);
  //           resolve();
  //         }
  //       }, 1000); // Check every second
  //     }
  //   });
  // }

  const paymentRequestsLoadedRef = useRef()
  const paymentRequestsRef = useRef()

  useEffect(() => {
    paymentRequestsLoadedRef.current = paymentRequestsLoaded
    paymentRequestsRef.current = paymentRequests
  }, [paymentRequestsLoaded, paymentRequests])

  const handleExport = async (segment) => {

    if(!isExportReady) {
      alert('Wait for data to be loaded. Please try again in a moment.')
      return
    }

    const csv = buildPurchasesExport({
      purchases: validPurchases,
      paymentRequests: paymentRequestsRef.current,
      genericProducts,
      specificProducts,
      localProducts,
      legacyProducts,
      resorts,
      domains,
      durations,
      regions,
      resellers,
      partners,
      rangeStart: segment ? exportRangeStart : null,
      rangeEnd: segment ? exportRangeEnd : null,
      userSegment: segment ? exportUserSegment : null,
    })
    const a = document.createElement('a')
    const fileName = 'Assuranceski-Purchases-Export'
    // print range in suffix or current date if no range in format DD-MM-YYYY
    const fileSuffix = segment && exportRangeStart && exportRangeEnd ? `${exportRangeStart}_${exportRangeEnd}` : `all-(${moment().format(EXPORT_DATE_FORMAT_SHORT)})`
    a.download = `${fileName}-${fileSuffix}.csv`
    a.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent(csv)
    a.click()
    setShowExportModal(false)
  }

  return (
    <>
      <ToolbarCustom
        pageTitle={'Purchases'}
        filterElements={[
        <div
          key={`filter-element-search`}
          data-kt-search-element='form'
          className='w-300px position-relative'
        >
          <KTSVG
            path='/media/icons/duotune/general/gen021.svg'
            className='svg-icon-2 svg-icon-lg-1 svg-icon-gray-500 position-absolute top-50 translate-middle-y ms-0'
          />
          <input
            type='text'
            className='form-control form-control-flush ps-9 pe-9'
            name='search'
            value={search}
            placeholder='Customer name, email, purchase ID'
            data-kt-search-element='input'
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* clear search field */}
          {search && (
            <div
              className='btn btn-flush btn-active-color-primary position-absolute top-50 ms-0 translate-middle-y end-0'
              onClick={() => setSearch('')}
            >
              <KTSVG
                path='/media/icons/duotune/arrows/arr061.svg'
                className='svg-icon-2 svg-icon-lg-1 svg-icon-gray-500'
              />
            </div>
          )}
        </div>,
        <div
          key={`filter-element-season`}
          className='d-flex align-items-center gap-2'
        >
          <span className='fw-semibold text-muted'>Season</span>
          <select
            className='form-select form-select-sm w-200px'
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            disabled={purchasesLoading}
          >
            {seasonOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>,
        <div
          key={`filter-element-export`}
          className='w-100 position-relative'
        >
          <div className='btn btn-primary btn-sm' onClick={promptExport}>Export</div>
        </div>]}
        loading={purchasesLoading || !purchasesLoaded}
        />
      {/* begin::Row */}

      <div className='mt-1'>
        <DataGridComponent
          id="purchases"
          rows={filteredPurchases.map(convertToTableItem)}
          loading={purchasesLoading}
          columns={[{
            field: 'product_label',
            headerName: 'Product',
            flex: 1,
          }, {
            field: 'id',
            headerName: 'Purchase ID',
          }, {
            field: 'resort_label',
            headerName: 'Resort',
          }, {
            field: 'domain_label',
            headerName: 'Domain',
          }, {
            field: 'duration_label',
            headerName: 'Duration',
          }, {
            field: 'amount',
            headerName: 'Amount',
            valueFormatter: (value) => `${(value / 100).toFixed(2)}€`,
          }, {
            field: 'customer_name',
            headerName: 'Customer',
            flex: 1,
          }, {
            field: 'customer_email',
            headerName: 'Email',
            flex: 1,
          }, {
            field: 'paid_at_label',
            headerName: 'Purchase date',
          }, {
            field: 'status',
            headerName: 'Refunded',
            valueFormatter: (value) => value === 'refunded' ? 'YES' : 'NO',
            width: 80,
          }, {
            field: 'voucher_code',
            headerName: 'Voucher code',
            width: 120,
          }, {
            field: 'documents_valid',
            headerName: 'Docs',
            width: 80,
            renderCell: ({ row }) => {
              return <span><i className={`bi fs-4 ${row.documents_valid ? 'bi-check2-circle text-success' : 'bi-x-circle text-danger'}`}></i></span>
            },
          }, {
            field: 'language_label',
            headerName: 'Language',
            width: 120,            
          }, {
            field: 'country_label',
            headerName: 'Country',
            width: 120,
          }, {
            field: 'payment_method_label',
            headerName: 'Payment method',
            width: 120,
          }, {
            field: 'api_purchase',
            headerName: 'Origin',
            renderCell: ({ row }) => {
              const isApiPurchase = Boolean(row.api_purchase)
              return (
                <span className={`badge ${isApiPurchase ? 'bg-success' : 'bg-primary'}`}>
                  {isApiPurchase ? 'API' : 'Web App'}
                </span>
              )
            },
            width: 120,
          }, {
            field: 'api_partner_label',
            headerName: 'API partner',
            width: 160,
          }, {
            field: 'metadata_activity',
            headerName: 'Metadata (activity)',
            width: 160,
          }, {
            field: 'metadata_region',
            headerName: 'Metadata (region)',
            width: 160,
          }, {
            field: 'actions',
            type: 'actions',
            headerName: '',
            cellClassName: 'actions',
            getActions: ({ id }) => {
              return [
                <GridActionsCellItem
                  key={`view-${id}`}
                  icon={<i className="bi bi-eye text-primary fs-4"></i>}
                  label="View"
                  className="textPrimary"
                  onClick={() => onViewClick(itemWithId(filteredPurchases, id))}
                  color="inherit"
                />,
              ];
            },
            width: 60
          }]}
          pageSizeOptions={[20, 50, 100]}
        />
      </div>
      
      <PurchaseDetailModal
        show={Boolean(currentPurchase)}
        handleClose={() => setCurrentPurchase(null)}
        purchase={currentPurchase} />
      {/** begin:export prompt modal */}
      {/** prompt to select dates from - to */ }
      <>
        <div
          className={`modal fade ${showExportModal ? 'show' : 'pe-none'} d-block`}
          id='exportModal'
          tabIndex='-1'
          aria-hidden={true}
        >
          <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg'>
            <div className={`modal-content ${!showExportModal ? 'pe-none' : ''}`}>
              <div className='modal-header'>
                <h2 className='fw-bolder'>Export Purchases</h2>
                <div className='btn btn-icon btn-sm btn-active-icon-primary' data-bs-dismiss='modal' onClick={() => setShowExportModal(false)}>
                  <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
                </div>
              </div>
              <div className='modal-body'>
                {/* {loadingExport && (
                  <div className='d-flex flex-column align-items-center justify-content-center mb-5'>
                    <div className='text-center mb-2'>
                      <div className='spinner-border text-primary' role='status'>
                      </div>
                    </div>
                    <span className=''>Loading export...</span>
                  </div>
                )} */}
                <div className='d-flex flex-column'>
                  <div className='fw-bold fs-6 mb-3'>Select date range (optional)</div>
                  <div className='d-flex flex-column'>
                    <div className='d-flex flex-row mb-3'>
                      <div className='d-flex flex-column flex-grow-1 pe-3'>
                        <label className='fw-bold fs-6 mb-1'>From</label>
                        <input type='date' className='form-control form-control-solid' onChange={(e) => setExportRangeStart(e.target.value)} />
                      </div>
                      <div className='d-flex flex-column flex-grow-1 ps-3'>
                        <label className='fw-bold fs-6 mb-1'>To</label>
                        <input type='date' className='form-control form-control-solid' onChange={(e) => setExportRangeEnd(e.target.value)} />
                      </div>
                    </div>
                  </div>
                  <div className='mt-4'>
                    <UserSegmentEditor
                      title="User segment (optional)"
                      products={[...purchaseSummaryGenericProducts.map(p => ({...p, type: 'generic' })), ...purchaseSummarySpecificProducts.map(p => ({...p, type: 'specific' }))]}
                      resorts={purchaseSummaryResorts}
                      domains={purchaseSummaryDomains}
                      durations={purchaseSummaryDurations}
                      languages={purchaseSummaryLanguages}
                      countries={purchaseSummaryCountries}
                      paymentMethods={purchaseSummaryPaymentMethods}
                      resellers={purchaseSummaryResellers}
                      regions={purchaseSummaryRegions}
                      partners={partners}
                      segment={exportUserSegment}
                      onSegmentChange={(segment) => setExportUserSegment(segment)}
                    />
                  </div>
                </div>
              </div>
              <div className='modal-footer flex-center'>
                <button
                  type='button'
                  className='btn btn-light btn-active-light-primary fw-bold me-2'
                  data-bs-dismiss='modal'
                  onClick={() => setShowExportModal(false)}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-primary fw-bold'
                  onClick={() => handleExport(true)}
                  // disabled={loadingExport}
                >
                  Export segment
                </button>
                <button
                  type='button'
                  className='btn btn-primary fw-bold'
                  onClick={() => handleExport(false)}
                  // disabled={loadingExport}
                  >
                    Export all
                  </button>
              </div>
            </div>
          </div>
        </div>
        <div className={`modal-backdrop fade ${showExportModal ? 'show' : 'pe-none'}`}></div>
      </>
    </>
  )
}

const PurchasesWrapper = () => {
  return (
    <>
      <PurchasesPage />
    </>
  )
}

export default PurchasesWrapper
